import { Delta } from 'quill/core';
import DiffAccordion from '../formats/DiffAccordion';
import { DiffStatus } from './diff';
const DEFAULT_DIFF_CONTEXT_LINES = 2;
function isChangedDiffStatus(op) {
    const diffStatus = op.attributes?.diffStatus;
    return diffStatus === DiffStatus.INSERTED || diffStatus === DiffStatus.DELETED;
}
function appendOpToDelta(delta, op) {
    if (op.insert !== undefined) {
        delta.insert(op.insert, op.attributes);
    }
    else if (op.retain) {
        delta.retain(op.retain, op.attributes);
    }
    else if (op.delete) {
        delta.delete(op.delete);
    }
}
function splitDeltaIntoLines(delta) {
    const lines = [{ ops: [], hasChanges: false }];
    const finalizeLine = () => {
        lines.push({ ops: [], hasChanges: false });
    };
    for (let i = 0; i < delta.ops.length; i++) {
        const op = delta.ops[i];
        const isChanged = isChangedDiffStatus(op);
        lines[lines.length - 1].hasChanges =
            lines[lines.length - 1].hasChanges || isChanged;
        if (typeof op.insert === 'string') {
            const parts = op.insert.split('\n');
            for (let j = 0; j < parts.length; j++) {
                const part = parts[j];
                const activeLine = lines[lines.length - 1];
                if (part) {
                    activeLine.ops.push({
                        insert: part,
                        attributes: op.attributes,
                    });
                }
                if (j < parts.length - 1) {
                    activeLine.ops.push({
                        insert: '\n',
                        attributes: op.attributes,
                    });
                    finalizeLine();
                    lines[lines.length - 1].hasChanges =
                        lines[lines.length - 1].hasChanges || isChanged;
                }
            }
            continue;
        }
        lines[lines.length - 1].ops.push(op);
    }
    const lastLine = lines[lines.length - 1];
    if (lastLine.ops.length === 0 && lines.length > 1) {
        lines.pop();
    }
    return lines;
}
function getMergedRanges(changedLineIndexes, totalLines, contextLines) {
    if (!changedLineIndexes.length) {
        return [];
    }
    const ranges = changedLineIndexes.map(lineIndex => ({
        start: Math.max(0, lineIndex - contextLines),
        end: Math.min(totalLines - 1, lineIndex + contextLines),
    }));
    const mergedRanges = [ranges[0]];
    for (let i = 1; i < ranges.length; i++) {
        const currentRange = ranges[i];
        const prevRange = mergedRanges[mergedRanges.length - 1];
        if (currentRange.start <= prevRange.end + 1) {
            prevRange.end = Math.max(prevRange.end, currentRange.end);
        }
        else {
            mergedRanges.push(currentRange);
        }
    }
    return mergedRanges;
}
function getHiddenLinesLabel(hiddenLines) {
    return hiddenLines === 1 ? 'Show 1 line' : `Show ${hiddenLines} lines`;
}
function cloneOp(op) {
    return {
        ...op,
        attributes: op.attributes ? { ...op.attributes } : undefined,
    };
}
function getHiddenRangeOps(lines, startLine, endLine) {
    const hiddenOps = [];
    for (let lineIndex = startLine; lineIndex <= endLine; lineIndex++) {
        const line = lines[lineIndex];
        for (let opIndex = 0; opIndex < line.ops.length; opIndex++) {
            hiddenOps.push(cloneOp(line.ops[opIndex]));
        }
    }
    return hiddenOps;
}
function insertCollapsedLinesMarker(delta, theme, lines, hiddenLines, startLine, endLine) {
    if (hiddenLines <= 0) {
        return;
    }
    const hiddenLabel = getHiddenLinesLabel(hiddenLines);
    const value = {
        title: hiddenLabel,
        body: { ops: getHiddenRangeOps(lines, startLine, endLine) },
    };
    delta.insert({ [DiffAccordion.blotName]: value });
}
export default function getCollapsedDiffViewDelta(theme, diffViewDelta, contextLines = DEFAULT_DIFF_CONTEXT_LINES) {
    const lines = splitDeltaIntoLines(diffViewDelta);
    const changedLineIndexes = [];
    for (let i = 0; i < lines.length; i++) {
        if (lines[i].hasChanges) {
            changedLineIndexes.push(i);
        }
    }
    const mergedRanges = getMergedRanges(changedLineIndexes, lines.length, contextLines);
    const collapsedDelta = new Delta();
    const hasLeadingGap = mergedRanges.length > 0 && mergedRanges[0].start > 0;
    if (hasLeadingGap) {
        const hiddenLeadingLines = mergedRanges[0].start;
        insertCollapsedLinesMarker(collapsedDelta, theme, lines, hiddenLeadingLines, 0, mergedRanges[0].start - 1);
    }
    for (let i = 0; i < mergedRanges.length; i++) {
        const range = mergedRanges[i];
        for (let lineIndex = range.start; lineIndex <= range.end; lineIndex++) {
            const line = lines[lineIndex];
            for (let opIndex = 0; opIndex < line.ops.length; opIndex++) {
                appendOpToDelta(collapsedDelta, line.ops[opIndex]);
            }
        }
        const hasMoreHunks = i < mergedRanges.length - 1;
        if (hasMoreHunks) {
            const nextRange = mergedRanges[i + 1];
            const hiddenLinesBetween = nextRange.start - range.end - 1;
            insertCollapsedLinesMarker(collapsedDelta, theme, lines, hiddenLinesBetween, range.end + 1, nextRange.start - 1);
        }
    }
    const hasTrailingGap = mergedRanges.length > 0 &&
        mergedRanges[mergedRanges.length - 1].end < lines.length - 1;
    if (hasTrailingGap) {
        const hiddenTrailingLines = lines.length - mergedRanges[mergedRanges.length - 1].end - 1;
        insertCollapsedLinesMarker(collapsedDelta, theme, lines, hiddenTrailingLines, mergedRanges[mergedRanges.length - 1].end + 1, lines.length - 1);
    }
    return collapsedDelta;
}
